"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var decamelize_1 = require("../utils/decamelize");
/**
 * Mark property with @prop decorator to run renderCallback() of CustomElement whenever property changes
 */
function prop() {
    return function (proto, propName, desc) {
        setOwnProperty(proto.constructor, '_observedProperties', {});
        proto.constructor._observedProperties[propName] = { propToAttribute: function (attr) { return ' '; } };
        var nativeGetter;
        var nativeSetter;
        if (desc) {
            // ts passes desc only if decorator applied to getter/setter
            // seems Object.defineProperty cannot override it in this case
            nativeGetter = desc.get;
            nativeSetter = desc.set;
            desc.get = getter;
            desc.set = setter;
        }
        else {
            Object.defineProperty(proto, propName, {
                configurable: true,
                enumerable: true,
                get: getter,
                set: setter
            });
        }
        function getter() {
            if (nativeGetter) {
                return nativeGetter.call(this);
            }
            else {
                return this.props[propName];
            }
        }
        function setter(newProp) {
            if (nativeSetter) {
                nativeSetter.call(this, newProp);
            }
            var oldProp = this.props[propName];
            this.props[propName] = newProp;
            this.propsChangedCallback(propName, oldProp, newProp);
        }
    };
}
exports.prop = prop;
/**
 * Create decorator with custom (de)serialization to be applied on a property of CustomElement
 */
function createPropertyDecorator(definition) {
    return function (proto, propName, desc) {
        definition.propToAttribute = definition.propToAttribute || decamelize_1.decamelize;
        setOwnProperty(proto.constructor, '_observedAttributes', []);
        proto.constructor._observedAttributes.push(definition.propToAttribute(propName));
        setOwnProperty(proto.constructor, '_observedProperties', {});
        proto.constructor._observedProperties[propName] = definition;
        var nativeGetter;
        var nativeSetter;
        if (desc) {
            // ts passes desc only if decorator applied to getter/setter
            // seems Object.defineProperty cannot override it in this case
            nativeGetter = desc.get;
            nativeSetter = desc.set;
            desc.get = getter;
            desc.set = setter;
        }
        else {
            Object.defineProperty(proto, propName, {
                configurable: true,
                enumerable: true,
                get: getter,
                set: setter
            });
        }
        function getter() {
            if (nativeGetter) {
                return nativeGetter.call(this);
            }
            else {
                if (this.props[propName] == null && this._defaultProps && this._defaultProps[propName] != null) {
                    return this._defaultProps[propName];
                }
                return this.props[propName];
            }
        }
        function setter(value) {
            if (nativeSetter) {
                nativeSetter.call(this, value);
            }
            // if serialize changes attr it will run attrChangedCb and lifecycle
            // sync with attrs happens in attrChangedCb
            if (this.elementConnected) {
                definition.serialize(this, propName, value);
            }
            else if (this.props[propName] !== value) {
                // according to spec we cannot set attr when in constructor
                // so reset them in connectedCb
                this.props[propName] = value;
                this._reserializeProps = this._reserializeProps || [];
                this._reserializeProps.push(propName);
                this._defaultProps = this._defaultProps || {};
                this._defaultProps[propName] = value;
            }
        }
    };
}
exports.createPropertyDecorator = createPropertyDecorator;
var string = createPropertyDecorator({
    serialize: function (element, propName, newProp) {
        var attrName = decamelize_1.decamelize(propName);
        if (newProp === null || newProp === undefined) {
            element.removeAttribute(attrName);
        }
        else {
            element.setAttribute(attrName, newProp);
        }
    },
    deserialize: function (element, propName) {
        var attrName = decamelize_1.decamelize(propName);
        if (element.hasAttribute(attrName)) {
            return element.getAttribute(attrName);
        }
        else {
            return null;
        }
    }
});
var boolean = createPropertyDecorator({
    serialize: function (element, propName, newProp) {
        var attrName = decamelize_1.decamelize(propName);
        if (newProp) {
            element.setAttribute(attrName, '');
        }
        else {
            element.removeAttribute(attrName);
        }
    },
    deserialize: function (element, propName) {
        var attrName = decamelize_1.decamelize(propName);
        return element.hasAttribute(attrName);
    }
});
var number = createPropertyDecorator({
    serialize: function (element, propName, newProp) {
        var attrName = decamelize_1.decamelize(propName);
        if (newProp === null || newProp === undefined) {
            element.removeAttribute(attrName);
        }
        else {
            element.setAttribute(attrName, newProp);
        }
    },
    deserialize: function (element, propName) {
        var attrName = decamelize_1.decamelize(propName);
        if (element.hasAttribute(attrName)) {
            return parseFloat(element.getAttribute(attrName));
        }
        else {
            return null;
        }
    }
});
/**
 * Set of decorators to make component rerender on property change
 * and to map attributes to properties and vice versa
 */
exports.props = {
    string: string,
    boolean: boolean,
    number: number,
    object: function (proto, propName) {
        console.warn('@props.object doesn\'t sync attrs with props and is deprecated. Use @prop() instead');
        return prop()(proto, propName);
    },
    array: function (proto, propName) {
        console.warn('@props.array doesn\'t sync attrs with props and is deprecated. Use @prop() instead');
        return prop()(proto, propName);
    },
    any: function (proto, propName) {
        console.warn('@props.any doesn\'t sync attrs with props and is deprecated. Use @prop() instead');
        return prop()(proto, propName);
    }
};
/**
 * Set default value as own property if object doesn't have it
 */
function setOwnProperty(object, propName, defaultValue) {
    if (!object.hasOwnProperty(propName)) {
        Object.defineProperty(object, propName, {
            value: defaultValue
        });
    }
}
